import { createAppAsyncThunk } from "appThunk";
import { logError } from "modules/helpers/logger/loggerSlice";
import { Partner } from "./partner";
import { Operator } from "./operator";
import { DateTime } from "luxon";
import { cubeProductQueryFilterHelper } from "modules/helpers/cube/cubeSlice";
import { ResultSet } from "@cubejs-client/core";
import { ProductSales } from "./productSales";
import { Range } from "./storeRangeFilters/range";

export class Store {
    public readonly id: string;
    public readonly name: string;
    public readonly address: string;
    public readonly region: string;
    public readonly clientRegion: string;
    public readonly retailCentreID: number;
    public readonly outputAreaCode: string;
    public readonly storeCategoryID: number;
    public readonly latitude: number;
    public readonly longitude: number;
    public readonly numberOfEmployees: number;
    public readonly openingDate: DateTime;
    public readonly sizeInSquareFeet: number;
    public readonly group: string | null;
    public readonly format: string | null;
    public readonly segment: string | null;
    public readonly sales: ProductSales;

    constructor(
        id: string,
        name: string,
        address: string,
        region: string,
        clientRegion: string,
        retailCentreID: number,
        outputAreaCode: string,
        storeCategoryID: number,
        latitude: number,
        longitude: number,
        numberOfEmployees: number,
        openingDate: DateTime,
        sizeInSquareFeet: number,
        group: string | null,
        format: string | null,
        segment: string | null,
        clientSourcedSales: number,
        estimatedSales: number,
        optimisedSales: number,
        salesHeadroom: number,
        confidenceInterval: number
    ) {
        this.id = id;
        this.name = name;
        this.address = address;
        this.region = region;
        this.clientRegion = clientRegion;
        this.retailCentreID = retailCentreID;
        this.outputAreaCode = outputAreaCode;
        this.storeCategoryID = storeCategoryID;
        this.latitude = latitude;
        this.longitude = longitude;
        this.numberOfEmployees = numberOfEmployees;
        this.openingDate = openingDate;
        this.sizeInSquareFeet = sizeInSquareFeet;
        this.group = group;
        this.format = format;
        this.segment = segment;
        this.sales = new ProductSales(
            id,
            name,
            clientSourcedSales,
            estimatedSales,
            optimisedSales,
            salesHeadroom,
            confidenceInterval
        );
    }
}

export const loadStores = createAppAsyncThunk<
    Store[],
    { referenceDate: DateTime, partner?: Partner, selectedStore?: Store, selectedRange?: Range, operator?: Operator }
>(
    "customer/tools/product/loadStores",
    async ({ referenceDate, partner, selectedStore, selectedRange, operator }, thunkAPI) => {
        try {
            if (!partner) {
                return [];
            }

            const startDate = referenceDate.minus({ months: 12 }).plus({ days: 1 }).startOf('day');
            const endDate = referenceDate.endOf('day');
            const query = {
                measures: [
                    "ProductSalesEstimate.SumClientSourcedSales",
                    "ProductSalesEstimate.SumEstimatedSales",
                    "ProductSalesEstimate.SumOptimisedSales",
                    "ProductSalesEstimate.SumSalesHeadroom",
                    "ProductSalesEstimate.AvgConfidenceInterval"
                ],
                timeDimensions: [{
                    dimension: "D_Date.Date",
                    dateRange: [startDate, endDate]
                }],
                dimensions: [
                    "D_Store.StoreNaturalID",
                    "D_Store.StoreName",
                    "D_Store.AddressLine1",
                    "D_Store.k_Region",
                    "D_Store.ClientRegion",
                    "D_Store.StoreCategory_ID",
                    "D_Store.RetailCentreID",
                    "D_Store.OutputAreaID",
                    "D_Store.Lat",
                    "D_Store.Long",
                    "D_Store.EmployeeCount",
                    "D_Store.OpeningDate",
                    "D_Store.Sqft",
                    "D_Store.Group",
                    "D_Store.Format",
                    "D_Store.Segment"
                ],
                filters: [],
                segments: [
                    "D_Store.OpenPhysicalStores"
                ],
            };

            const resultSet = await thunkAPI.dispatch(
                cubeProductQueryFilterHelper(query, operator, partner, selectedStore, selectedRange)
            ) as unknown as ResultSet;
            const stores = resultSet.rawData().map(row => new Store(
                row["D_Store.StoreNaturalID"],
                row["D_Store.StoreName"],
                row["D_Store.AddressLine1"],
                row["D_Store.k_Region"],
                row["D_Store.ClientRegion"],
                Number(row["D_Store.RetailCentreID"]),
                row["D_Store.OutputAreaID"],
                Number(row["D_Store.StoreCategory_ID"]),
                Number(row["D_Store.Lat"]),
                Number(row["D_Store.Long"]),
                Number(row["D_Store.EmployeeCount"]),
                DateTime.fromISO(row["D_Store.OpeningDate"], { zone: "utc" }),
                Number(row["D_Store.Sqft"]),
                row["D_Store.Group"],
                row["D_Store.Format"],
                row["D_Store.Segment"],
                Number(row["ProductSalesEstimate.SumClientSourcedSales"]),
                Number(row["ProductSalesEstimate.SumEstimatedSales"]),
                Number(row["ProductSalesEstimate.SumOptimisedSales"]),
                Number(row["ProductSalesEstimate.SumSalesHeadroom"]),
                Number(row["ProductSalesEstimate.AvgConfidenceInterval"])
            ));
            return stores;
        } catch (error) {
            thunkAPI.dispatch(logError("Error loading stores."));
            return thunkAPI.rejectWithValue(null);
        }
    }
);

export const loadFilterStores = createAppAsyncThunk<
    Store[],
    { referenceDate: DateTime, partner?: Partner, operator?: Operator }
>(
    "customer/tools/product/loadFilterStores",
    async ({ referenceDate, partner, operator }, thunkAPI) => {
        try {
            if (!partner) {
                return [];
            }

            const startDate = referenceDate.minus({ months: 12 }).plus({ days: 1 }).startOf('day');
            const endDate = referenceDate.endOf('day');
            const query = {
                measures: [
                    "ProductSalesEstimate.SumClientSourcedSales",
                    "ProductSalesEstimate.SumEstimatedSales",
                    "ProductSalesEstimate.SumOptimisedSales",
                    "ProductSalesEstimate.SumSalesHeadroom",
                    "ProductSalesEstimate.AvgConfidenceInterval"
                ],
                timeDimensions: [{
                    dimension: "D_Date.Date",
                    dateRange: [startDate, endDate]
                }],
                dimensions: [
                    "D_Store.StoreNaturalID",
                    "D_Store.StoreName",
                    "D_Store.AddressLine1",
                    "D_Store.k_Region",
                    "D_Store.ClientRegion",
                    "D_Store.StoreCategory_ID",
                    "D_Store.RetailCentreID",
                    "D_Store.OutputAreaID",
                    "D_Store.Lat",
                    "D_Store.Long",
                    "D_Store.EmployeeCount",
                    "D_Store.OpeningDate",
                    "D_Store.Sqft",
                    "D_Store.Group",
                    "D_Store.Format",
                    "D_Store.Segment"
                ],
                filters: [],
                segments: [
                    "D_Store.OpenPhysicalStores"
                ],
            };

            const resultSet = await thunkAPI.dispatch(
                cubeProductQueryFilterHelper(query, operator, partner, undefined, undefined)
            ) as unknown as ResultSet;
            const stores = resultSet.rawData().map(row => new Store(
                row["D_Store.StoreNaturalID"],
                row["D_Store.StoreName"],
                row["D_Store.AddressLine1"],
                row["D_Store.k_Region"],
                row["D_Store.ClientRegion"],
                Number(row["D_Store.RetailCentreID"]),
                row["D_Store.OutputAreaID"],
                Number(row["D_Store.StoreCategory_ID"]),
                Number(row["D_Store.Lat"]),
                Number(row["D_Store.Long"]),
                Number(row["D_Store.EmployeeCount"]),
                DateTime.fromISO(row["D_Store.OpeningDate"], { zone: "utc" }),
                Number(row["D_Store.Sqft"]),
                row["D_Store.Group"],
                row["D_Store.Format"],
                row["D_Store.Segment"],
                Number(row["ProductSalesEstimate.SumClientSourcedSales"]),
                Number(row["ProductSalesEstimate.SumEstimatedSales"]),
                Number(row["ProductSalesEstimate.SumOptimisedSales"]),
                Number(row["ProductSalesEstimate.SumSalesHeadroom"]),
                Number(row["ProductSalesEstimate.AvgConfidenceInterval"])
            ));
            return stores;
        } catch (error) {
            thunkAPI.dispatch(logError("Error loading filter stores."));
            return thunkAPI.rejectWithValue(null);
        }
    }
);
